import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { TEST_IDS } from "../constants/testIds";
import { Trash2, Upload, LogOut } from "lucide-react";

export default function AdminDashboard() {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    title: "",
    location: "",
    description: "",
    file: null,
  });
  const [uploading, setUploading] = useState(false);

  const refresh = async () => {
    try {
      const r = await api.get("/cantieri", { withCredentials: false });
      setItems(r.data || []);
    } catch (e) {
      /* noop */
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/auth/me");
        setMe(r.data);
        await refresh();
      } catch (e) {
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* noop */
    }
    navigate("/admin/login");
  };

  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(r.result);
      r.onerror = reject;
      r.readAsDataURL(file);
    });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.file) {
      toast.error("Titolo e immagine sono obbligatori.");
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(form.file);
      await api.post("/cantieri", {
        title: form.title,
        location: form.location || null,
        description: form.description || null,
        image_base64: base64,
      });
      toast.success("Cantiere pubblicato.");
      setForm({ title: "", location: "", description: "", file: null });
      document.getElementById("upload-file-input").value = "";
      await refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Upload non riuscito.");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo cantiere?")) return;
    try {
      await api.delete(`/cantieri/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Rimosso.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-white/50 font-mono text-sm tracking-widest uppercase">
        Caricamento…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0C10]">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-[#0A0C10]/90 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <div className="relative w-8 h-8 shrink-0">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_foxtherm-radianti/artifacts/sgmszqf1_logo_trasp.png"
                alt="Foxtherm"
                className="absolute inset-0 w-full h-full object-contain"
              />
            </div>
            <span className="font-display font-black uppercase text-sm tracking-tighter">
              Fox<span className="text-[#E31E24]">therm</span>
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40 border-l border-white/15 pl-3 ml-3">
                Admin
              </span>
            </span>
          </Link>
          <div className="flex items-center gap-6">
            <span className="hidden sm:inline font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
              {me?.email}
            </span>
            <button
              data-testid={TEST_IDS.admin.logout}
              onClick={logout}
              className="btn-ghost"
            >
              <LogOut size={14} />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-6 md:px-10 py-12">
        <div className="grid grid-cols-12 gap-8">
          {/* Upload form */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="col-span-12 lg:col-span-5"
          >
            <div className="border border-white/10 p-8 bg-[#12151C]">
              <div className="eyebrow mb-4">
                <span className="inline-block mr-3 w-6 h-px bg-[#E31E24] align-middle" />
                Nuovo cantiere
              </div>
              <h2 className="font-display font-black uppercase text-3xl tracking-tighter mb-8">
                Carica una foto
              </h2>
              <form
                data-testid={TEST_IDS.admin.uploadForm}
                onSubmit={submit}
                className="space-y-6"
              >
                <div>
                  <label className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40 block mb-2">
                    Titolo *
                  </label>
                  <input
                    className="input-underline"
                    required
                    data-testid={TEST_IDS.admin.uploadTitle}
                    value={form.title}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, title: e.target.value }))
                    }
                    placeholder="Es. Villa privata — pavimento fresato"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40 block mb-2">
                    Località
                  </label>
                  <input
                    className="input-underline"
                    data-testid={TEST_IDS.admin.uploadLocation}
                    value={form.location}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, location: e.target.value }))
                    }
                    placeholder="Es. Verona"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40 block mb-2">
                    Descrizione
                  </label>
                  <textarea
                    className="input-underline resize-none"
                    rows={3}
                    data-testid={TEST_IDS.admin.uploadDescription}
                    value={form.description}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, description: e.target.value }))
                    }
                    placeholder="Note tecniche o fasi del lavoro"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] tracking-[0.28em] uppercase text-white/40 block mb-2">
                    Immagine *
                  </label>
                  <input
                    id="upload-file-input"
                    type="file"
                    accept="image/*"
                    required
                    data-testid={TEST_IDS.admin.uploadFile}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))
                    }
                    className="block w-full text-sm text-white/70 file:mr-4 file:py-3 file:px-5 file:border file:border-white/20 file:bg-transparent file:text-white file:font-mono file:text-[11px] file:uppercase file:tracking-[0.2em] file:cursor-pointer hover:file:border-[#E31E24] hover:file:text-[#E31E24] file:transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  data-testid={TEST_IDS.admin.uploadSubmit}
                  disabled={uploading}
                  className="btn-heat disabled:opacity-60"
                >
                  <Upload size={14} />
                  {uploading ? "Caricamento…" : "Pubblica"}
                </button>
              </form>
            </div>
          </motion.section>

          {/* Gallery */}
          <section className="col-span-12 lg:col-span-7">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-display font-black uppercase text-2xl tracking-tighter">
                Galleria — {items.length}
              </h2>
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/40">
                Live
              </span>
            </div>
            {items.length === 0 ? (
              <div className="border border-dashed border-white/15 p-14 text-center text-white/50">
                Nessun cantiere caricato. Aggiungine uno per iniziare.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-white/10 border border-white/10">
                {items.map((it) => (
                  <div
                    key={it.id}
                    className="relative bg-[#0A0C10] aspect-[4/5] overflow-hidden group"
                  >
                    <img
                      src={it.image_url}
                      alt={it.title}
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0C10] via-[#0A0C10]/40 to-transparent" />
                    <div className="absolute inset-0 p-5 flex flex-col justify-between">
                      <div className="flex justify-end">
                        <button
                          data-testid={TEST_IDS.admin.deleteItem(it.id)}
                          onClick={() => remove(it.id)}
                          className="w-9 h-9 border border-white/30 bg-[#0A0C10]/60 backdrop-blur-sm text-white/70 hover:text-white hover:border-[#E31E24] hover:bg-[#E31E24] flex items-center justify-center transition-colors"
                          title="Elimina"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div>
                        <div className="font-mono text-[9px] tracking-[0.3em] uppercase text-[#F03B41] mb-1">
                          {it.location || "—"}
                        </div>
                        <div className="font-display font-black uppercase text-base tracking-tight leading-tight">
                          {it.title}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
